import React, { createRef, Fragment, useEffect, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { ExpandCollapse } from '@tds/core-expand-collapse';
import DimpleDivider from '@tds/core-dimple-divider';
import { colorTelusPurple } from '@tds/core-colours';
import _ from 'lodash';

import request from '../utils/request';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import {
  getMultiCheckboxOptions,
  findField,
  getFieldName,
  getFieldValue,
  getFieldType,
  getFieldChecked
} from './utils/formCommon';
import StatusBar from '../iot/statusBar';
import { Box } from '../design/css';
import {
  GroupTitle,
  GroupWrapper,
  GroupWrapperStyled,
  SummaryGroupWrapper
} from './css';
import {
  iotPrequalificationForm,
  iotOrderForm,
  fieldIotAccountType,
  fieldIotNewOrExisting,
  fieldIotAccessAccountViaSmartDesktop,
  fieldIotCreditCheckPassInCarat,
  fieldIotAddonSpecificSubscriber,
  fieldIotAddonSubscriberList,
  fieldIotVehicleOptions,
  fieldIotAdditionalAttachements,
  fieldIotAdditionalComment,
  fieldIotTelusAccountNumber,
  fieldIotActivationOrRenwal,
  fieldIotProductSelected,
  fieldCaratCreditCheckFailMessage,
  fieldIotIncorporationNumber,
  fieldIotIncorporationDate,
  fieldIotSubscribersRenewalOptions,
  fieldIotCaratCaseId,
  fieldIotManagersEmailApproval
} from '../utils/constant';
import useFetchContentfulData from '../hooks/useFetchContentfulData';

import UserDetailsForm from './userDetailsForm';
import Note from '../note';
import FormSummaryGroup from './formSummaryGroup';
import RepeatedForm from './repeatedForm';
import stringInterpolation from '../utils/stringInterpolation';
import FormField from './formField';
import ViewsToggle from '../iot/orderDetailsSection/viewsToggle';
import RatePlans from '../iot/orderDetailsSection/ratePlans';
import Addons from '../iot/orderDetailsSection/addonSection';
import AdditionalServices from '../iot/orderDetailsSection/additionalServices';
import Hardware from '../iot/orderDetailsSection/hardware';
import VehicleInfo from '../iot/orderDetailsSection/vehicleInfo';
import SummaryTracker from '../iot/summaryTracker';
import RatePlansRenewal from '../iot/orderDetailsSection/ratePlansRenewal';

const formFieldsGroup = 'formFieldsGroup';
const formSection = 'formSection';
const userDetailsKey = 'userDetails';
const repeatedFormKey = 'repeatedForm';
const requiredValue = 'required';
const profile = request.getProfile();

const FormStepIoT = ({
  stepData,
  setEligible,
  onSaveAndExit,
  orderData,
  setOrderData,
  formName,
  createNewIotOrder,
  onFormSubmit
}) => {
  if (!stepData) return null;

  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: {
      yes,
      no,
      valueNew,
      valueExisting,
      valueBusinessRegularMedium,
      valueCorporate,
      value2year,
      value3year,
      valueRenewal,
      valueGeotab,
      valueFleetcomplete,
      valueRaven,
      valueNewDeviceDbWaiver80,
      valueEnterListManually
    },
    modalForm: { fieldRequiredMessage, cannotProceedMessage },
    formStep: {
      minValueErrorMessage,
      maxValueErrorMessage,
      minLengthErrorMessage,
      maxLengthErrorMessage
    },
    formButtons: { newOrderButtonLabel }
  } = localizedStrings;
  const {
    fields: { name: formStepName, formStepFields, formSummary, modalForms }
  } = stepData;

  const [isLoading, setIsLoading] = useState(true);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isFormValidated, setIsFormValidated] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [sections, setSections] = useState([]);
  const [dueNow, setDueNow] = useState(0);
  const [dueMonthly, setDueMonthly] = useState(0);
  const [isFieldReset, setIsFieldReset] = useState(false);
  const [prequalEligibility, setPrequalEligibility] = useState(false);
  const [viewType, setViewType] = useState();
  const [ratePlans, setRatePlans] = useState({});
  const [renewalSubscriberList, setRenewalSubscriberList] = useState([]);
  const [addons, setAddons] = useState({});
  const [additionalServices, setAdditionalServices] = useState({});
  const [hardware, setHardware] = useState({});
  const [vehicleInfo, setVehicleInfo] = useState([]);
  const [total, setTotal] = useState({
    ratePlan: 0,
    subscriberList: 0,
    addons: 0,
    additionalServices: 0,
    hardware: 0
  });

  const viewTypeRef = useRef();

  const getIotOrderDetilsKey = () => {
    const valueProductSelected = _.get(orderData, fieldIotProductSelected, '');
    let orderDetailsKey;
    switch (valueProductSelected) {
      case valueGeotab:
        orderDetailsKey = 'ioTGeoTabOrderDetails';
        break;
      case valueFleetcomplete:
        orderDetailsKey = 'ioTFleetCompleteOrderDetails';
        break;
      case valueRaven:
        orderDetailsKey = 'ioTRavenOrderDetails';
        break;
      default:
        orderDetailsKey = 'ioTFleetCompleteOrderDetails';
        break;
    }
    return orderDetailsKey;
  };
  const { data: contentfulData, error: contentfulError } =
    orderData && useFetchContentfulData(getIotOrderDetilsKey());

  const formStepRef = createRef();

  const formSections = formStepFields.filter(({ sys }) => {
    const {
      contentType: {
        sys: { id }
      }
    } = sys;

    return id === formSection;
  });

  const open = sections.reduce((acc, section) => {
    const {
      fields: { name, isPanelInitiallyOpen, formFields: sectionFields }
    } = section;

    if (isPanelInitiallyOpen) {
      acc.push(name);
    } else {
      const isErrors =
        sectionFields &&
        sectionFields.find(item => {
          const {
            fields: { name: itemName }
          } = item;
          return formErrors[itemName];
        });
      if (typeof isErrors !== 'undefined') acc.push(name);
    }

    return acc;
  }, []);

  const getDefaultValue = (key, value) => {
    if (orderData && orderData.id) return orderData[key];
    return value || '';
  };

  const normalizedFields = (data, initFields) => {
    let result = initFields;
    if (data) {
      data.forEach(item => {
        const { sys, fields } = item;
        const {
          name,
          type,
          required,
          value,
          minValue,
          maxValue,
          minLength,
          maxLength,
          price,
          monthlyPayment,
          options,
          tooltip,
          noteAppearsBelowField,
          customFieldErrorMessage
        } = fields;
        const {
          contentType: {
            sys: { id }
          }
        } = sys;
        const key = name;
        const ref = createRef();

        if (id === formFieldsGroup) {
          result = normalizedFields(fields.fields, result);
        } else if (id === formSection) {
          result = normalizedFields(fields.formFields, result);
        } else if (id === userDetailsKey) {
          result = normalizedFields(fields.formStepFields, result);
        } else if (id === repeatedFormKey) {
          const formObjectKeys = fields.formFields.reduce(
            (acc, { fields: { name: formNameKey, value: defaultValue } }) => {
              acc[formNameKey] = defaultValue;
              return acc;
            },
            {}
          );
          result[fields.key] = [formObjectKeys];
        } else if (!Object.prototype.hasOwnProperty.call(result, key)) {
          const defaultValue = getDefaultValue(key, value);
          let updatedMonthlyPayment = monthlyPayment;
          let updatedPrice = price;

          if (['Select', 'Radio'].includes(type)) {
            if (options && defaultValue) {
              const currentOption = options.find(
                o => o.fields.value === defaultValue
              );

              if (typeof currentOption !== 'undefined') {
                updatedPrice = currentOption.fields.price;
                updatedMonthlyPayment = currentOption.fields.monthlyPayment;
              }
            }
          }

          if (type === 'MultiCheckbox') {
            if (defaultValue && defaultValue.length > 0 && options) {
              const updateDefaultValue =
                typeof defaultValue === 'string'
                  ? defaultValue.split(',')
                  : defaultValue;
              const mappedValues = updateDefaultValue.reduce(
                (ac, a) => ({ ...ac, [a]: true }),
                {}
              );
              updatedPrice = options.reduce(
                (ac, a) =>
                  ac + ((mappedValues[a.fields.value] && a.fields.price) || 0),
                0
              );
              updatedMonthlyPayment = options.reduce(
                (ac, a) =>
                  ac +
                  ((mappedValues[a.fields.value] && a.fields.monthlyPayment) ||
                    0),
                0
              );
            }
          }

          result[key] = {
            name,
            ref,
            type,
            value: defaultValue || value,
            initPrice: updatedPrice || 0,
            price: updatedPrice || 0,
            initMonthlyPayment: updatedMonthlyPayment || 0,
            monthlyPayment: updatedMonthlyPayment || 0,
            minValue,
            maxValue,
            minLength,
            maxLength,
            isRequired: required || false,
            isVisible: true,
            options,
            tooltip,
            noteAppearsBelowField,
            customFieldErrorMessage
          };

          if (type === 'Checkbox') {
            result[key].checked = defaultValue;
          }

          if (
            type === 'PlusMinusInput' &&
            defaultValue === undefined &&
            value
          ) {
            result[key].value = value;
          }
        }
      });
    }

    return result;
  };

  const isManagersEmailApprovalRequired = () =>
    renewalSubscriberList.some(
      subscriber => subscriber.offer === valueNewDeviceDbWaiver80
    );

  const customChecks = (fieldName, fields) => {
    const valueProductSelected = _.get(orderData, fieldIotProductSelected, '');
    const valueAccountType = getFieldValue(fieldIotAccountType, formFields);
    const valueNewOrExisting = getFieldValue(fieldIotNewOrExisting, formFields);
    const valueAccountAccessViaSmartDesktop = getFieldValue(
      fieldIotAccessAccountViaSmartDesktop,
      fields
    );
    const valueCreditCheckPass = getFieldValue(
      fieldIotCreditCheckPassInCarat,
      formFields
    );

    /**
     * Managers Email Approval
     */
    if (
      fieldName === fieldIotManagersEmailApproval &&
      !isManagersEmailApprovalRequired()
    )
      return false;

    /**
     * Customer Migrating
     */
    if (
      fieldName === 'CustomerMigrating' &&
      [valueFleetcomplete, valueRaven].includes(valueProductSelected)
    )
      return false;

    /**
     * Credit check pass in CARAT
     */
    if (
      fieldName === fieldIotCreditCheckPassInCarat &&
      !(
        (valueAccountType === valueBusinessRegularMedium &&
          valueNewOrExisting &&
          valueAccountAccessViaSmartDesktop === yes) ||
        valueProductSelected === valueRaven
      )
    )
      return false;

    if (
      ([
        fieldIotAddonSpecificSubscriber,
        fieldIotAddonSubscriberList,
        fieldIotVehicleOptions,
        fieldIotAdditionalAttachements,
        fieldIotAdditionalComment
      ].includes(fieldName) &&
        !viewType) ||
      ([fieldIotAddonSpecificSubscriber, fieldIotAddonSubscriberList].includes(
        fieldName
      ) &&
        viewType !== 'advancedView')
    )
      return false;

    /**
     * CARAT/CAR Error Message
     */
    if (
      fieldName === fieldCaratCreditCheckFailMessage &&
      !(
        ([valueGeotab, valueFleetcomplete].includes(valueProductSelected) &&
          valueAccountType === valueBusinessRegularMedium &&
          valueCreditCheckPass === no) ||
        (valueProductSelected === valueRaven && valueCreditCheckPass === no)
      )
    )
      return false;

    /**
     * Incorporation Number
     * Incorporation Date
     */
    if (
      [fieldIotIncorporationNumber, fieldIotIncorporationDate].includes(
        fieldName
      ) &&
      !(
        ([valueGeotab, valueFleetcomplete].includes(valueProductSelected) &&
          valueNewOrExisting === valueNew &&
          valueAccountAccessViaSmartDesktop === no) ||
        (valueProductSelected === valueRaven && valueAccountType)
      )
    )
      return false;

    /**
     * CARAT/CAR Case ID
     */
    if (
      fieldName === fieldIotCaratCaseId &&
      !(
        ([valueGeotab, valueFleetcomplete].includes(valueProductSelected) &&
          valueAccountType === valueBusinessRegularMedium &&
          valueCreditCheckPass === yes) ||
        (valueProductSelected === valueRaven && valueCreditCheckPass === yes)
      )
    )
      return false;

    /**
     * Telus Account Number
     */
    if (
      fieldName === fieldIotTelusAccountNumber &&
      !(
        (valueAccountType === valueBusinessRegularMedium ||
          valueProductSelected === valueRaven) &&
        valueCreditCheckPass === yes
      ) &&
      !(
        valueNewOrExisting === valueExisting &&
        valueAccountAccessViaSmartDesktop === no
      )
    )
      return false;

    if (fieldName === fieldIotSubscribersRenewalOptions && !viewType)
      return false;
    return true;
  };

  const isRelatedFieldExisted = (field, fields) => {
    const {
      name: fieldName,
      appearsAfterField,
      appearsAfterValue,
      backendFieldValidation
    } = field;

    const passCustomChecks = customChecks(fieldName, fields);
    if (!passCustomChecks) return false;

    if (!appearsAfterField && !backendFieldValidation) return true;
    let result = true;
    let i = 0;
    if (appearsAfterField) {
      while (i < appearsAfterField.length && result) {
        const relatedFieldName = getFieldName(
          appearsAfterField[i],
          orderData,
          formName,
          yes
        );
        const relatedFieldType = getFieldType(relatedFieldName, fields);
        const relatedFieldValue =
          relatedFieldType === 'Checkbox'
            ? getFieldChecked(relatedFieldName, fields)
            : getFieldValue(relatedFieldName, fields);
        if (relatedFieldName) {
          if (
            fields[relatedFieldName] &&
            fields[relatedFieldName].isVisible &&
            relatedFieldValue
          ) {
            if (appearsAfterValue) {
              let isValueSelected;
              if (_.isArray(relatedFieldValue)) {
                isValueSelected = appearsAfterValue.find(item =>
                  relatedFieldValue.includes(item.fields.value)
                );
              } else {
                isValueSelected = appearsAfterValue.find(
                  item => relatedFieldValue === item.fields.value
                );
              }
              result = result && typeof isValueSelected !== 'undefined';
            }
          } else if (orderData && orderData[relatedFieldName]) {
            if (fields[relatedFieldName]) {
              if (
                (isFormTouched &&
                  orderData[relatedFieldName] === relatedFieldValue) ||
                !isFormTouched
              ) {
                if (appearsAfterValue) {
                  const isValueSelected = appearsAfterValue.find(
                    item => orderData[relatedFieldName] === item.fields.value
                  );
                  result = result && typeof isValueSelected !== 'undefined';
                }
              } else result = false;
            } else if (appearsAfterValue) {
              const isValueSelected = appearsAfterValue.find(
                item => orderData[relatedFieldName] === item.fields.value
              );
              result = result && typeof isValueSelected !== 'undefined';
            }
          } else {
            result = false;
          }
        }
        i += 1;
      }
    }

    if (backendFieldValidation) {
      let isVisible = result;
      for (let index = 0; index < backendFieldValidation.length; index += 1) {
        const {
          fields: { key, value }
        } = backendFieldValidation[index];
        isVisible =
          isVisible &&
          (value.split('|').includes(orderData[key]) ||
            (value.split('|').includes('undefined') &&
              typeof orderData[key] === 'undefined'));
      }
      return isVisible;
    }

    return result;
  };

  const getHiddenSections = fields => {
    const hiddenSections = [];

    formSections.forEach(section => {
      const {
        fields: { name: sectionName }
      } = section;
      if (!isRelatedFieldExisted(section.fields, fields)) {
        hiddenSections.push(sectionName);
      }
    });

    return hiddenSections;
  };

  const updateSections = updatedSections => {
    let newSections = [...formSections];
    updatedSections.forEach(item => {
      newSections = newSections.filter(section => section.fields.name !== item);
    });
    setSections(newSections);
  };

  const isDeliverySectionAvailable = () => {
    let isHardwareSelected = false;
    Object.keys(ratePlans).forEach(plan => {
      Object.keys(_.get(ratePlans, plan, {})).forEach(device => {
        const deviceDetails = _.get(ratePlans, `${plan}.${device}`, '');
        if (
          [value2year, value3year].includes(deviceDetails.term) &&
          deviceDetails.quantity > 0
        )
          isHardwareSelected = true;
      });
    });
    if (!isHardwareSelected) {
      const totalHardwareQuantity = Object.keys(hardware).reduce(
        (acc, device) => {
          return acc + hardware[device].additionalQuantity;
        },
        0
      );
      isHardwareSelected = totalHardwareQuantity > 0;
    }
    return isHardwareSelected;
  };

  const sectionIsVisible = name => {
    if (name === 'iotDeliveryDetails') return isDeliverySectionAvailable();
    if (name === 'iotVehicleInfo') return Boolean(viewType);

    return (
      typeof sections.find(item => item.fields.name === name) !== 'undefined'
    );
  };
  const getRepeatedFormErrors = (contentfulFields, formValues) => {
    const fields = contentfulFields.filter(
      ({ fields: content }) => content.required
    );
    const filteredData = formValues.filter(formValue => {
      const filteredValues = fields.filter(
        ({ fields: { name: formKeyValue } }) => !formValue[formKeyValue]
      );
      return filteredValues.length > 0;
    });
    return filteredData;
  };

  const msgMinValue = value => stringInterpolation(minValueErrorMessage, value);
  const msgMaxValue = value => stringInterpolation(maxValueErrorMessage, value);
  const msgMinLength = value =>
    stringInterpolation(minLengthErrorMessage, value);
  const msgMaxLength = value =>
    stringInterpolation(maxLengthErrorMessage, value);

  const isOneDeviceSelected = deviceList => {
    return Object.keys(deviceList).some(deviceName => {
      const { quantity, term } = deviceList[deviceName];
      return term && quantity;
    });
  };

  const validateRatePlanList = () => {
    return Object.keys(ratePlans).every(planName => {
      const allDevicesSelectionValid = Object.keys(ratePlans[planName]).every(
        deviceName => {
          const { fiftyPercentInUS, quantity, term } = ratePlans[planName][
            deviceName
          ];
          const oneDeviceSelected = fiftyPercentInUS
            ? isOneDeviceSelected(ratePlans[planName])
            : false;
          // console.log(planName, oneDeviceSelected);
          let allConditionsValid = true;
          if (fiftyPercentInUS && !oneDeviceSelected && !term)
            allConditionsValid = false;
          if (term && !quantity) allConditionsValid = false;

          return allConditionsValid;
        }
      );
      return allDevicesSelectionValid;
    });
  };

  const getErrors = (data, errors) => {
    let result = errors;
    if (!data) return result;
    data.forEach(({ sys, fields }) => {
      const {
        contentType: {
          sys: { id }
        }
      } = sys;
      const { ineligible } = fields;
      const key = fields.name;
      const {
        value,
        minValue,
        maxValue,
        isRequired,
        isVisible,
        ref,
        minLength,
        maxLength,
        type,
        checked
      } =
        formFields &&
        Object.prototype.hasOwnProperty.call(formFields, key) &&
        formFields[key];

      /** Delete key if it exists because of an earlier error */
      if (key in result) delete result[key];

      if (id === formFieldsGroup && isRelatedFieldExisted(fields, formFields)) {
        result = getErrors(fields.fields, result);
      } else if (id === formSection && sectionIsVisible(key)) {
        result = getErrors(fields.formFields, result);
      } else if (id === userDetailsKey) {
        result = getErrors(fields.formStepFields, result);
      } else if (id === repeatedFormKey) {
        const repeatedFormErrors = getRepeatedFormErrors(
          fields.formFields,
          formFields[fields.key]
        );
        if (repeatedFormErrors.length) {
          result[fields.key] = repeatedFormErrors;
        } else {
          delete result[fields.key];
        }
      } else if (isVisible) {
        if (isRequired && type !== 'Checkbox') {
          if (!value && isRelatedFieldExisted(fields, formFields)) {
            result[key] = { text: fieldRequiredMessage, ref };
          } else {
            delete result[key];
          }
        }
        if (ineligible) {
          const {
            fields: { value: ineligibleValue }
          } = ineligible;
          const isIneligibleValue = ineligibleValue.split('|').includes(value);
          if (isIneligibleValue) {
            result[key] = { text: cannotProceedMessage, ref };
          }
        }
        if (isRequired && type === 'Checkbox') {
          if (!checked && isRelatedFieldExisted(fields, formFields))
            result[key] = { text: fieldRequiredMessage, ref };
          else delete result[key];
        }

        if (typeof minValue !== 'undefined' && parseFloat(value) < minValue) {
          result[key] = { text: msgMinValue(minValue), ref };
        }
        if (typeof maxValue !== 'undefined' && parseFloat(value) > maxValue) {
          result[key] = { text: msgMaxValue(maxValue), ref };
        }
        if (minLength && value && value.length < minLength) {
          result[key] = { text: msgMinLength(minLength), ref };
        }
        if (maxLength && value && value.length > maxLength) {
          result[key] = { text: msgMaxLength(maxLength), ref };
        }

        if (fields.fieldValidation && fields.fieldValidation.fields) {
          if (
            typeof fields.appearsAfterField === 'undefined' ||
            (typeof fields.appearsAfterField !== 'undefined' &&
              isRelatedFieldExisted(fields, formFields))
          ) {
            const {
              errorMessage,
              logic,
              value: expectedValue
            } = fields.fieldValidation.fields;
            let hasError = false;
            switch (logic) {
              case 'equal':
                // eslint-disable-next-line eqeqeq
                hasError = !expectedValue.split('|').includes(value);
                break;
              case 'regex':
                {
                  const regexLogic = new RegExp(expectedValue);
                  hasError = !regexLogic.test(value);
                }
                break;
              case 'gt':
                hasError = parseInt(value, 10) < parseInt(expectedValue, 10);
                break;
              case 'gte':
                hasError = parseInt(value, 10) <= parseInt(expectedValue, 10);
                break;
              case 'lt':
                hasError = +value < +expectedValue;
                break;
              default:
            }
            if (hasError) result[key] = { text: errorMessage, ref };
          }
        }
      }
      /** View Type validation */
      if (key === 'viewToggleButtons' && !viewType)
        result[key] = { text: 'Please select a view type', ref };
      /** Rateplan validation */
      if (key === 'iotRatePlans') {
        if (!validateRatePlanList())
          result[key] = {
            text: 'Please select the term and unit for the selected rateplan',
            ref
          };
        else if (total.ratePlan === 0)
          result[key] = {
            text: 'Please select a rateplan',
            ref
          };
      }
      /** Rateplan Renewal validation */

      if (key === 'iotRatePlansRenewal') {
        const valueSubscriberRenewalOption = getFieldValue(
          fieldIotSubscribersRenewalOptions,
          formFields
        );

        if (
          valueSubscriberRenewalOption === valueEnterListManually &&
          renewalSubscriberList.length === 0
        )
          result[key] = {
            text: 'Please add subscriber details',
            ref
          };
      }
    });
    return result;
  };

  const getRateplanData = () => {
    const fleetTrackingServices = [];
    const assetTrackingServices = [];
    const videoTelematicServices = [];

    Object.keys(ratePlans).forEach(planName => {
      Object.keys(ratePlans[planName]).forEach(deviceName => {
        const {
          term,
          quantity,
          unitPrice,
          fiftyPercentInUS,
          rateplanType
        } = ratePlans[planName][deviceName];
        if (term && quantity) {
          if (rateplanType === 'Fleet Tracker')
            fleetTrackingServices.push({
              name: planName,
              device: deviceName,
              term,
              quantity,
              unitPrice,
              fiftyPercentInUS
            });
          if (rateplanType === 'Asset Tracker')
            assetTrackingServices.push({
              name: planName,
              device: deviceName,
              term,
              quantity,
              unitPrice,
              fiftyPercentInUS
            });
          if (rateplanType === 'Video Telematics')
            videoTelematicServices.push({
              name: planName,
              device: deviceName,
              term,
              quantity,
              unitPrice,
              fiftyPercentInUS
            });
        }
      });
    });
    return {
      fleetTrackingServices,
      assetTrackingServices,
      videoTelematicServices
    };
  };

  const getAddonsData = () => {
    const addonsServices = Object.keys(addons).map(addonName => {
      const { quantity, unitPrice } = addons[addonName];
      return {
        name: addonName,
        quantity,
        unitPrice
      };
    });

    return addonsServices;
  };

  const getHardwareData = () => {
    const hardwareList = [];
    const accessoryList = [];

    Object.keys(hardware).forEach(hardwareName => {
      const {
        includedQuantity,
        additionalQuantity,
        unitPrice,
        isAccessory
      } = hardware[hardwareName];

      if (isAccessory)
        accessoryList.push({
          name: hardwareName,
          includedQuantity,
          additionalQuantity,
          unitPrice
        });
      else
        hardwareList.push({
          name: hardwareName,
          includedQuantity,
          additionalQuantity,
          unitPrice
        });
    });

    return {
      hardwareList,
      accessoryList
    };
  };

  const getAdditionalServicesData = () => {
    const additonalServicesList = Object.keys(additionalServices).map(
      serviceName => {
        const { quantity, unitPrice } = additionalServices[serviceName];
        return {
          name: serviceName,
          quantity,
          unitPrice
        };
      }
    );

    return additonalServicesList;
  };

  const getFormData = () => {
    const result = Object.keys(formFields)
      .filter(key => typeof formFields[key].value !== 'undefined')
      .reduce((acc, key) => {
        const { type, value: oldValue, checked, isVisible } = formFields[key];
        const value = isVisible ? oldValue : '';
        acc[key] = type === 'Checkbox' ? checked : value;
        return acc;
      }, {});
    result.provinceOfTheOrder = _.get(result, 'iotProvince', '');
    result.viewType = viewType;
    result.ratePlan = ratePlans;
    const {
      fleetTrackingServices,
      assetTrackingServices,
      videoTelematicServices
    } = getRateplanData();
    result.fleetTrackingServices = fleetTrackingServices;
    result.assetTrackingServices = assetTrackingServices;
    result.videoTelematicServices = videoTelematicServices;
    result.renewalSubscriberList = renewalSubscriberList;
    result.addons = addons;
    result.addonsServices = getAddonsData();
    result.additonalServicesList = getAdditionalServicesData();
    result.additionalServices = additionalServices;
    result.hardware = hardware;
    const { hardwareList, accessoryList } = getHardwareData();
    result.hardwareList = hardwareList;
    result.accessoryList = accessoryList;
    result.vehicleInfo = vehicleInfo;
    result.total = total;
    getRateplanData();
    return result;
  };

  const saveAndExit = () => {
    const fields = getFormData();
    if (Object.entries(fields).length) {
      onSaveAndExit(fields);
    }
  };

  const onOrderSubmit = e => {
    e.preventDefault();
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      const fields = getFormData();
      onFormSubmit({ ...fields });
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  const resetFormFields = (fields, currentFieldName) => {
    const { resetFields, customResetFields } = fields[currentFieldName];
    let isResetRequired = false;
    const newFields = { ...fields };
    for (let index = 0; index < formStepFields.length; index += 1) {
      const {
        fields: { name, appearsAfterField, value }
      } = formStepFields[index];
      if (name === currentFieldName) {
        isResetRequired = true;
      } else if (isResetRequired) {
        const field = fields[name];
        if (appearsAfterField && field.isVisible)
          newFields[name] = { ...field, isVisible: false, value };
      }
    }
    if (resetFields) {
      resetFields.forEach(field => {
        const fieldName = _.get(field, 'fields.name', '');
        if (fieldName && _.has(newFields[fieldName], 'value'))
          newFields[fieldName].value = '';
      });
    }
    if (customResetFields) {
      customResetFields.forEach(field => {
        const fieldName = _.get(field, 'fields.key', '');
        if (fieldName && _.has(newFields[fieldName], 'value'))
          newFields[fieldName].value = '';
        else delete newFields[fieldName];
      });
    }
    return newFields;
  };

  const isFieldCalculated = (type, value, checked, price) => {
    switch (type) {
      case 'Radio':
        return value === yes && price;
      case 'Checkbox':
        return checked && price;
      default:
        return value && price;
    }
  };

  const updateTotals = (name, field) => {
    const { value, type, price, monthlyPayment, isVisible } = field;
    if (!isVisible) return;

    let oldQuantity;
    let newQuantity;
    let oldPrice;
    let newPrice;

    if (type === 'Number') {
      oldQuantity = (formFields[name] && formFields[name].value) || 0;
      newQuantity = value ? Number(value) : 0;
      if (price > 0) {
        oldPrice = price * oldQuantity;
        newPrice = price * newQuantity;
        setDueNow(dueNow - oldPrice + newPrice);
      }
      if (monthlyPayment > 0) {
        oldPrice = monthlyPayment * oldQuantity;
        newPrice = monthlyPayment * newQuantity;
        setDueMonthly(dueMonthly - oldPrice + newPrice);
      }
    }
  };

  const resetSelectedFields = (fields, isCustom = false) => {
    if (fields.length > 0) {
      const updatedOrderData = { ...orderData };
      fields.forEach(field => {
        const keyPath = isCustom ? 'fields.key' : 'fields.name';
        const fieldName = _.get(field, keyPath, '');
        if (_.has(orderData, fieldName)) {
          updatedOrderData[fieldName] = '';
        }
      });
      setIsFieldReset(true);
      setOrderData(updatedOrderData);
    }
  };

  const checkPrequalEligibility = () => {
    let isEligible = true;
    Object.keys(formFields).forEach(key => {
      const field = _.get(formStepFields, '0.fields.formFields', []).filter(
        item => item.fields.name === key
      );
      if (
        isRelatedFieldExisted(_.get(field, '0.fields', {}), formFields) &&
        !formFields[key].value
      )
        isEligible = false;
    });
    setPrequalEligibility(isEligible);
  };

  const handleFormField = (
    { name, value, checked },
    isFieldModified,
    fileUploded
  ) => {
    const fields = { ...formFields };
    let needToCheck = 1;
    const addAmountToNow = 0;
    const addAmountToMonthly = 0;
    let subtractAmountFromNow = 0;
    let subtractAmountFromMonthly = 0;

    setIsFieldReset(false);
    setIsFormValidated(false);

    if (isFieldModified && !isFormTouched) {
      setIsFormTouched(true);
    }

    fields[name] = {
      ...fields[name],
      value,
      checked,
      isVisible: true,
      touched: true
    };

    if (fields[name].type === 'MultiCheckbox') {
      fields[name].options = getMultiCheckboxOptions(
        fields[name].options,
        value,
        name,
        fields,
        orderData
      );
    }

    if (fields[name].type === 'MultiFileUpload') {
      fields[name] = { ...fields[name], fileUploded };
      formFields[name] = { ...formFields[name], fileUploded };
      formStepFields[name] = { ...formStepFields[name], fileUploded };
    }

    updateTotals(name, fields[name]);
    if (['Select', 'Radio'].includes(fields[name].type)) {
      const currentField = findField(formStepFields, name);
      if (currentField && currentField.fields && currentField.fields.options) {
        const currentOption = currentField.fields.options.find(
          item => item.fields.value === value
        );

        if (typeof currentOption !== 'undefined') {
          fields[name].price = currentOption.fields.price || 0;
          fields[name].monthlyPayment =
            currentOption.fields.monthlyPayment || 0;
        } else {
          if (fields[name].price) fields[name].price = 0;
          if (fields[name].monthlyPayment) fields[name].monthlyPayment = 0;
        }
      }
    }

    // Form sections visibility can be related to fields and values
    updateSections(getHiddenSections(fields));

    const getDisabledFields = (data, initFields) => {
      let result = initFields;

      const filter = !data
        ? []
        : data.filter(item => {
            const { sys, fields: itemFields } = item;
            const {
              name: itemName,
              appearsAfterField,
              eligible: eligibleField,
              ineligible,
              ineligibleNote
            } = itemFields;
            const {
              contentType: {
                sys: { id }
              }
            } = sys;
            if (name === itemName) {
              if (eligibleField) {
                const {
                  fields: { value: eligibleValue }
                } = eligibleField;
                setEligible({
                  isEligible:
                    eligibleValue === value ||
                    (eligibleValue === requiredValue && !!value && value !== 0),
                  note: {}
                });
              } else if (ineligible) {
                const {
                  fields: { value: ineligibleValue }
                } = ineligible;
                const isIneligibleValue = ineligibleValue
                  .split('|')
                  .includes(value);
                setEligible({
                  isEligible: false,
                  note: isIneligibleValue ? ineligibleNote : {}
                });
              }
            }
            if (id === formFieldsGroup) {
              result = getDisabledFields(itemFields.fields, result);
            } else if (id === formSection) {
              result = getDisabledFields(itemFields.formFields, result);
            } else if (fields[itemName] && appearsAfterField) {
              const isVisible = isRelatedFieldExisted(item.fields, fields);
              if (fields[itemName].isVisible) {
                fields[itemName].isVisible = isVisible;
                return !isVisible;
              }
              fields[itemName].isVisible = isVisible;
            }
            return false;
          });

      return [...result, ...filter];
    };

    while (needToCheck > 0 && needToCheck < 5) {
      const disabledFields = getDisabledFields(formStepFields, []);

      // eslint-disable-next-line no-loop-func
      disabledFields.forEach(item => {
        const {
          fields: { name: itemName }
        } = item;

        if (
          isFieldCalculated(
            fields[itemName].type,
            fields[itemName].value,
            fields[itemName].checked,
            fields[itemName].price
          )
        ) {
          const itemPrice =
            fields[itemName].type === 'Number'
              ? Number(fields[itemName].initPrice) *
                Number(fields[itemName].value)
              : Number(fields[itemName].price);
          subtractAmountFromNow += itemPrice;
        }

        if (
          isFieldCalculated(
            fields[itemName].type,
            fields[itemName].value,
            fields[itemName].checked,
            fields[itemName].monthlyPayment
          )
        ) {
          const itemPrice =
            fields[itemName].type === 'Number'
              ? Number(fields[itemName].initMonthlyPayment) *
                Number(fields[itemName].value)
              : Number(fields[itemName].monthlyPayment);
          subtractAmountFromMonthly += itemPrice;
        }
      });

      needToCheck = disabledFields.length > 0 ? needToCheck + 1 : 0;
    }

    if (addAmountToNow || subtractAmountFromNow) {
      setDueNow(dueNow - subtractAmountFromNow + addAmountToNow);
    }
    if (addAmountToMonthly || subtractAmountFromMonthly) {
      setDueMonthly(
        dueMonthly - subtractAmountFromMonthly + addAmountToMonthly
      );
    }

    setFormFields(resetFormFields(fields, name));

    if (_.has(fields, `${name}.resetFields`)) {
      resetSelectedFields(_.get(fields, `${name}.resetFields`, []));
    } else if (_.has(fields, `${name}.customResetFields`)) {
      resetSelectedFields(_.get(fields, `${name}.customResetFields`, []), true);
    }
  };

  const renderFormSummary = (formSummaryGroup, data = orderData) => {
    let summaryData = data;
    summaryData = { ...profile, ...summaryData };
    return <FormSummaryGroup data={formSummaryGroup} orderData={summaryData} />;
  };

  const getAccountType = options => {
    const valueProductSelected = _.get(orderData, fieldIotProductSelected, '');
    if ([valueGeotab, valueFleetcomplete].includes(valueProductSelected)) {
      const updatedOptions = options.filter(option =>
        [valueBusinessRegularMedium, valueCorporate].includes(
          option.fields.value
        )
      );
      return updatedOptions;
    }
    if (valueProductSelected === valueRaven) {
      const updatedOptions = options.filter(
        option => option.fields.value !== valueCorporate
      );
      return updatedOptions;
    }
    return options;
  };

  const renderFormFields = data =>
    data &&
    data.map(({ sys, fields }, index) => {
      const {
        contentType: {
          sys: { id }
        },
        id: sysId
      } = sys;
      const { name: fieldName, title, expiredDate, type, note2 } = fields;
      const fieldValue = getFieldValue(fieldName, formFields);
      const fieldDateConfig = {};
      let selectOptions = _.get(fields, 'options', []);
      const isRequired = fields.required || false;
      const fieldNote = fields.note;
      const fieldNote2 = note2;

      if (type === 'MultiCheckbox') {
        selectOptions = formFields[fieldName].options;
      }

      const error = formErrors[fieldName];
      const key = `FormField${fieldName}${index}${sysId}`;
      let result;

      if (expiredDate && new Date(expiredDate) < new Date()) return null;
      if (!isRelatedFieldExisted(fields, formFields)) return null;

      if (fieldName === fieldIotAccountType)
        selectOptions = [...getAccountType(selectOptions)];

      if (id === formFieldsGroup) {
        const groupIsVisible = fields.fields.find(item => {
          const { fields: itemFields } = item;
          if (!formFields[itemFields.name]) return null;
          const { value, isVisible } = formFields[itemFields.name];
          return (
            (value && isVisible) ||
            isRelatedFieldExisted(itemFields, formFields)
          );
        });

        if (typeof groupIsVisible !== 'undefined') {
          const groupFields = renderFormFields(fields.fields);
          if (fieldName === 'advisory') {
            result = groupFields.filter(item => item).length > 0 && (
              <GroupWrapperStyled key={key}>{groupFields}</GroupWrapperStyled>
            );
          } else {
            result = groupFields.filter(item => item).length > 0 && (
              <GroupWrapper key={key}>
                {title && <GroupTitle>{title}</GroupTitle>}
                {groupFields}
              </GroupWrapper>
            );
          }
        }
      } else if (id === formSection) {
        const { note, formSummaryGroup } = fields;
        result = sectionIsVisible(fieldName) && (
          <ExpandCollapse.Panel key={key} id={fieldName} header={title}>
            <>
              <DimpleDivider />
              {note && note.content && <Note data={note} type="richText" />}
              {formSummaryGroup
                ? renderFormSummary(formSummaryGroup)
                : renderFormFields(fields.formFields)}
            </>
          </ExpandCollapse.Panel>
        );
      } else if (id === userDetailsKey) {
        result = (
          <UserDetailsForm
            key={key}
            data={fields}
            formName={formName}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
            resetSelectedFields={resetSelectedFields}
          />
        );
      } else if (id === repeatedFormKey) {
        result = (
          <RepeatedForm
            key={key}
            data={fields}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
            isMultisiteOrder={false}
          />
        );
      } else if (fields.type === 'IoTViewToggle') {
        result = (
          <ViewsToggle
            viewType={viewType}
            setViewType={setViewType}
            formErrors={formErrors}
          />
        );
      } else if (fields.type === 'IoTRateplans') {
        result = contentfulData && viewType && (
          <>
            <DimpleDivider />
            <RatePlans
              orderType={_.get(orderData, fieldIotActivationOrRenwal, '')}
              productSelected={_.get(orderData, fieldIotProductSelected, '')}
              rateplanList={contentfulData.rateplanList}
              contentfulError={contentfulError}
              accountType={getFieldValue(fieldIotAccountType, formFields)}
              viewType={viewType}
              ratePlans={ratePlans}
              setRatePlans={setRatePlans}
              total={total.ratePlan}
              setTotal={setTotal}
              formErrors={formErrors}
            />
          </>
        );
      } else if (fields.type === 'IoTRateplansRenewal') {
        result = contentfulData && viewType && (
          <>
            <DimpleDivider />
            <RatePlansRenewal
              isCorporate={
                getFieldValue(fieldIotAccountType, formFields) ===
                valueCorporate
              }
              rateplanList={contentfulData.rateplanList}
              contentfulError={contentfulError}
              renewalSubscriberList={renewalSubscriberList}
              setRenewalSubscriberList={setRenewalSubscriberList}
              setTotal={setTotal}
              formErrors={formErrors}
            />
          </>
        );
      } else if (fields.type === 'IoTAddons') {
        const valueProduct = _.get(orderData, fieldIotProductSelected, '');
        const valueOrderType = _.get(orderData, fieldIotActivationOrRenwal, '');
        result = contentfulData &&
          viewType &&
          (viewType === 'advancedView' ||
            (valueProduct === valueFleetcomplete &&
              valueOrderType === valueRenewal)) && (
            <>
              <DimpleDivider />
              <Addons
                addonsList={contentfulData.addonsList}
                contentfulError={contentfulError}
                viewType={viewType}
                addons={addons}
                setAddons={setAddons}
                total={total.addons}
                setTotal={setTotal}
              />
              <DimpleDivider />
            </>
          );
      } else if (fields.type === 'IoTAdditionalServices') {
        result = contentfulData && viewType && viewType === 'advancedView' && (
          <>
            <DimpleDivider />
            <AdditionalServices
              additionalServicesList={contentfulData.additionalServicesList}
              contentfulError={contentfulError}
              viewType={viewType}
              additionalServices={additionalServices}
              setAdditionalServices={setAdditionalServices}
              total={total.additionalServices}
              setTotal={setTotal}
            />
            <DimpleDivider />
          </>
        );
      } else if (fields.type === 'IoTAdditionalServices') {
        result = contentfulData && viewType && viewType === 'advancedView' && (
          <>
            <DimpleDivider />
            <AdditionalServices
              additionalServicesList={contentfulData.additionalServicesList}
              contentfulError={contentfulError}
              viewType={viewType}
              additionalServices={additionalServices}
              setAdditionalServices={setAdditionalServices}
              total={total.additionalServices}
              setTotal={setTotal}
            />
            <DimpleDivider />
          </>
        );
      } else if (fields.type === 'IoTHardware') {
        const valueProduct = _.get(orderData, fieldIotProductSelected, '');
        result = contentfulData &&
          viewType &&
          (viewType === 'advancedView' ||
            (valueProduct === valueRaven && viewType === 'simpleView')) && (
            <>
              <DimpleDivider />
              <Hardware
                hardwareList={contentfulData.hardwareList}
                contentfulError={contentfulError}
                isRenewal={
                  _.get(orderData, fieldIotActivationOrRenwal, '') ===
                  valueRenewal
                }
                viewType={viewType}
                ratePlans={ratePlans}
                hardware={hardware}
                setHardware={setHardware}
                total={total.hardware}
                setTotal={setTotal}
              />
              <DimpleDivider />
            </>
          );
      } else if (fields.type === 'IoTVehicleInfo') {
        result = viewType && (
          <>
            <DimpleDivider />
            <VehicleInfo
              vehicleInfo={vehicleInfo}
              setVehicleInfo={setVehicleInfo}
            />
            <DimpleDivider />
          </>
        );
      } else if (fields.type === 'IoTSummaryTracker') {
        result = contentfulData && (
          <SummaryTracker
            rateplanList={contentfulData.rateplanList}
            hasAdditonalServices={_.get(
              contentfulData,
              'additionalServicesList',
              ''
            )}
            ratePlans={ratePlans}
            addons={addons}
            additionalServices={additionalServices}
            hardware={hardware}
            vehicleInfo={vehicleInfo}
          />
        );
      } else {
        const fieldButtonProps =
          fields.type === 'Button'
            ? {
                modalForms,
                formFields,
                setFormFields,
                dueNow,
                setDueNow,
                dueMonthly,
                setDueMonthly,
                setIsFormTouched,
                setIsFormValidated
              }
            : {};
        let ref;
        let isDisabled;
        if (formFields[fieldName]) {
          ({ ref, isDisabled } = formFields[fieldName]);
        }

        const fieldData = {
          ...fields,
          options: selectOptions || fields.options,
          ...fieldButtonProps,
          ref,
          name: fieldName,
          value: fieldValue,
          disabled: isDisabled,
          required: isRequired,
          note: fieldNote,
          note2: fieldNote2,
          sysId,
          onChange: (e, isFieldModified = true, fileUploded) =>
            handleFormField(e.target, isFieldModified, fileUploded),
          fieldDateConfig,
          formFields,
          setFormFields,
          setOrderData,
          resetSelectedFields
        };

        if (fields.type === 'Checkbox') {
          fieldData.checked = formFields[fieldName].checked;
        }

        result = (
          <FormField
            key={key}
            orderData={orderData}
            data={fieldData}
            error={error && error.text}
          />
        );
      }
      return result;
    });

  const getSummary = () => {
    if (formSummary) {
      const {
        fields: { formSummaryGroup }
      } = formSummary;

      return formSummaryGroup.map((summaryGroup, index) => {
        const {
          fields: { name, formFields: summaryGroupFields }
        } = summaryGroup;
        const key = `formSummaryGroup${index}`;

        return (
          <Fragment key={key}>
            <h1>{name}</h1>
            <SummaryGroupWrapper>
              {summaryGroupFields.map((summaryGroupField, idx) => {
                const {
                  fields: { name: fieldName, label: fieldLabel }
                } = summaryGroupField;
                const summaryGroupFieldKey = `summaryGroupField${idx}`;

                return (
                  <div key={summaryGroupFieldKey}>
                    <span style={{ fontWeight: '400' }}>{fieldLabel}</span>:{' '}
                    {formFields[fieldName].value}
                  </div>
                );
              })}
            </SummaryGroupWrapper>
          </Fragment>
        );
      });
    }

    return Object.keys(formFields).map((item, index) => {
      const key = `summary${index}`;

      return (
        formFields[item].isVisible && (
          <div key={key}>
            <span style={{ fontWeight: '400' }}>{item}</span>:{' '}
            {formFields[item].value}
          </div>
        )
      );
    });
  };

  const onCreateNewOrder = () => {
    const formData = getFormData();
    createNewIotOrder(formData);
  };

  useEffect(() => {
    if (formStepFields && !isFieldReset) {
      if (formName === iotOrderForm && !_.get(orderData, 'id', '')) return;
      const updatedFields = normalizedFields(formStepFields, formFields);
      setFormFields(updatedFields);
      updateSections(getHiddenSections(updatedFields));

      if (orderData.viewType) setViewType(orderData.viewType);
      if (orderData.ratePlan) setRatePlans(orderData.ratePlan);
      if (orderData.renewalSubscriberList)
        setRenewalSubscriberList(orderData.renewalSubscriberList);
      if (orderData.addons) setAddons(orderData.addons);
      if (orderData.additionalServices)
        setAdditionalServices(orderData.additionalServices);
      if (orderData.hardware) setHardware(orderData.hardware);
      if (orderData.vehicleInfo) setVehicleInfo(orderData.vehicleInfo);

      setIsLoading(false);
      setIsFormTouched(false);

      if (formStepRef && formStepRef.current)
        formStepRef.current.scrollTo(0, 0);
    }
  }, [formStepFields, orderData]);

  useEffect(() => {
    setDueMonthly(total.ratePlan + total.subscriberList + total.addons);
    setDueNow(total.hardware + total.additionalServices);
  }, [total]);

  useEffect(() => {
    /**
     * This condition checks if viewType had a previous value
     * and that value was not null
     */

    if (viewTypeRef.current) {
      setRatePlans({});
      setAddons({});
      setAdditionalServices({});
      setHardware({});
      setVehicleInfo([]);
      setRenewalSubscriberList([]);
      setTotal({
        ratePlan: 0,
        subscriberList: 0,
        additionalServices: 0,
        addons: 0,
        hardware: 0
      });
    }
    viewTypeRef.current = viewType;
  }, [viewType]);

  useEffect(() => {
    setIsFormTouched(true);
  }, [ratePlans, hardware, vehicleInfo]);

  useEffect(() => {
    if (formName === iotPrequalificationForm) checkPrequalEligibility();
  }, [formFields]);

  useEffect(() => {
    document.getElementById('footer').style.display = 'none';
    document.getElementById('introduction').style.marginBottom = '7rem';

    return () => {
      document.getElementById('footer').style.display = '';
      document.getElementById('introduction').style.marginBottom = '0';
    };
  }, []);

  if (isLoading) return null;
  return (
    <div ref={formStepRef} style={{ width: '100%' }}>
      {formName === iotOrderForm && (
        <div style={{ marginBottom: '2rem' }}>
          <div id="title">
            <div id="title-wrapper">
              <div style={{ padding: '2rem 0 0 0' }}>
                <h2 style={{ color: colorTelusPurple, fontSize: '30px' }}>
                  {formStepName}
                </h2>
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ backgroundColor: '#fff' }}>
        <form
          name={formStepName}
          onSubmit={e => e.preventDefault()}
          encType="multipart/form-data"
        >
          <Box
            style={{
              border: '1px solid #e3e3e3',
              boxShadow: '1px 1px 9px 4px #e3e3e3',
              padding: '2rem',
              marginBottom: '1rem'
            }}
          >
            {sections.length ? (
              <div className="expand">
                <ExpandCollapse tag="h2" topDivider={false} open={open}>
                  {renderFormFields(formStepFields)}
                </ExpandCollapse>
              </div>
            ) : (
              <div style={{ width: '100%' }}>
                {formStepFields ? (
                  renderFormFields(formStepFields)
                ) : (
                  <>
                    <h3 style={{ marginBottom: '1em' }}>Summary</h3>
                    {getSummary()}
                  </>
                )}
              </div>
            )}
          </Box>
        </form>
      </div>
      {formName === iotPrequalificationForm && (
        <div
          className="container 85%"
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <button
            type="button"
            onClick={onCreateNewOrder}
            className={`frg-button ${
              prequalEligibility ? 'color-green' : 'state-disabled'
            }`}
            style={{ marginBottom: '1em' }}
          >
            {newOrderButtonLabel}
          </button>
        </div>
      )}
      {formName === iotOrderForm && (
        <StatusBar
          isFormTouched={isFormTouched}
          isFormValidated={isFormValidated}
          dueMonthly={dueMonthly}
          dueNow={dueNow}
          onSaveAndExit={saveAndExit}
          onSubmit={onOrderSubmit}
          formErrors={formErrors}
          hidePricing={
            getFieldValue(fieldIotAccountType, formFields) === valueCorporate
          }
        />
      )}
    </div>
  );
};

FormStepIoT.propTypes = {
  stepData: PropTypes.shape({
    fields: PropTypes.objectOf(PropTypes.object).isRequired
  }).isRequired,
  setEligible: PropTypes.func,
  onSaveAndExit: PropTypes.func,
  orderData: PropTypes.objectOf(PropTypes.any),
  setOrderData: PropTypes.func,
  formName: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  createNewIotOrder: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired
};

FormStepIoT.defaultProps = {
  setEligible: null,
  orderData: {},
  onSaveAndExit: null,
  setOrderData: () => {}
};

export default FormStepIoT;
